'use client';

import {
  CategoryCarouselItemType,
  CategoryCarouselType
} from '@theme/widgets/category-carousel';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Scrollbar } from 'swiper/modules';
import { Link } from '@theme/components';
import ViewTracker from '@theme/components/ViewTracker';
import { Image } from '@akinon/next/components';
import { pushSelectPromotion } from '@theme/utils/gtm';
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/free-mode';
import { useMediaQuery } from '@theme/hooks';

const getGtmItem = (
  item: CategoryCarouselItemType,
  i: number,
  locationId: string
) => ({
  promotion_id: item.value.promotion_id || `kategoriler_${i + 1}`,
  promotion_name:
    item.value.promotion_name || item.value.title || `Kategori ${i + 1}`,
  creative_name: item.kwargs.value.image.url,
  creative_slot: `slot_${i + 1}`,
  location_id: locationId
});

const CarouselItem = ({
  item,
  index,
  locationId
}: {
  item: CategoryCarouselItemType;
  index: number;
  locationId: string;
}) => (
  <>
    <Link
      className="flex items-center justify-center"
      href={item.value.link}
      onClick={() =>
        pushSelectPromotion({
          ecommerce: { items: [getGtmItem(item, index, locationId)] }
        })
      }
    >
      <Image
        src={item.kwargs.value.image.url}
        alt={item.value.title}
        draggable={false}
        width={180}
        height={180}
        className="w-full max-w-[180px] h-[180px] object-cover"
        imageClassName="w-full w-[180px] h-[180px] object-cover"
        priority={index < 7}
        loading={index < 7 ? 'eager' : 'lazy'}
      />
    </Link>
    <Link
      className="pt-2 pb-1 text-primary-black text-center text-sm font-normal flex justify-center lg:text-base"
      href={item.value.link}
      onClick={() =>
        pushSelectPromotion({
          ecommerce: { items: [getGtmItem(item, index, locationId)] }
        })
      }
    >
      {item.value.title}
    </Link>
  </>
);

export default function CategoryCarouselContent({
  slug,
  attributes
}: {
  slug?: string;
  attributes: CategoryCarouselType;
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const items = attributes?.items ?? [];
  const gridOnMobile = attributes?.grid_on_mobile?.value !== 'no';
  const locationId = attributes.location_id?.value || 'anasayfa_kategoriler_banner';
  const useGrid = isMobile === true && gridOnMobile;

  return (
    <div className="flex flex-col -ml-2.5 md:ml-0">
      {attributes?.title?.value && (
        <div className="flex items-center mb-6 px-5 justify-between text-primary-black lg:mb-8 lg:justify-normal md:px-0">
          <h2 className="text-2xl font-medium leading-7 xl:text-3xl 4xl:text-[2rem] 4xl:leading-10">
            {attributes.title.value}
          </h2>
        </div>
      )}
      <ViewTracker
        eventType="view_promotion"
        eventData={{
          ecommerce: {
            items: items.map((item, i) => getGtmItem(item, i, locationId))
          }
        }}
      >
        <Swiper
          className="category-carousel-swiper"
          modules={[FreeMode, Scrollbar]}
          spaceBetween={6}
          slidesPerView="auto"
          freeMode={true}
          slidesOffsetBefore={isMobile ? 20 : 0}
          slidesOffsetAfter={isMobile ? 20 : 0}
          scrollbar={{ hide: false, enabled: true }}
          style={{ paddingBottom: '20px' } as unknown}
        >
          {useGrid
            ? Array.from(
                { length: Math.ceil(items.length / 2) },
                (_, si) => {
                  const first = items[si * 2];
                  const second = items[si * 2 + 1];
                  return (
                    <SwiperSlide
                      key={si}
                      style={{ width: '180px', marginRight: '6px' }}
                    >
                      <div className="flex flex-col gap-1.5">
                        <CarouselItem
                          item={first}
                          index={si * 2}
                          locationId={locationId}
                        />
                        {second && (
                          <CarouselItem
                            item={second}
                            index={si * 2 + 1}
                            locationId={locationId}
                          />
                        )}
                      </div>
                    </SwiperSlide>
                  );
                }
              )
            : items.map((item, i) => (
                <SwiperSlide
                  key={item.kwargs.value.image.url}
                  style={{ width: '180px', marginRight: '6px' }}
                >
                  <CarouselItem item={item} index={i} locationId={locationId} />
                </SwiperSlide>
              ))}
        </Swiper>
      </ViewTracker>
    </div>
  );
}
